import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Modal,
  Button,
  TextField,
  IconButton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import toast from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import axiosInstant from '../../../api';
import { CustomFieldLabel } from '../../../components/fields/CustomFieldLabel/CustomFieldLabel';

type Mode = 'create' | 'edit' | 'duplicate';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  mode: Mode;
  apiBase?: string;
  label?: string;
  navigateTo?: string;
  shortlistId?: number | null;
  currentName?: string;
  currentDownloadEnabled?: boolean;
  fetchShortlists?: () => void;
  updateShortlistName?: (name: string) => void;
  onShortlistCreated?: () => void;
  onDownloadEnabledChange?: (enabled: boolean) => void;
}

const textFieldStyles = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  height: '50px',
  gap: 2,
  alignSelf: 'stretch',
  border: 'none',
  borderRadius: '12px',
  backgroundColor: 'var(--Base-24, #F4F4F4)',
  '& .MuiOutlinedInput-root': {
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' },
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'var(--Base-300, #B0B0B0)',
    fontFamily: 'CircularStd',
    fontSize: 14,
    fontWeight: 450,
  },
  '& .MuiInputBase-input': {
    color: 'var(--Base-1000, #000)',
    fontFamily: 'CircularStd',
    fontSize: 14,
    fontWeight: 500,
  },
  '& .MuiInputBase-input:focus': {
    outline: 'none',
    boxShadow: 'none',
    border: 'none',
  },
  '& .MuiInputBase-input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0px 1000px var(--Base-24, #F4F4F4) inset !important',
    backgroundColor: 'var(--Base-24, #F4F4F4) !important',
    color: 'var(--Base-1000, #000) !important',
    border: 'none !important',
    transition: 'background-color 5000s ease-in-out 0s',
  },
};

const TITLES: Record<Mode, (label: string) => string> = {
  create: (label) => `Create ${label}`,
  edit: (label) => `Edit ${label}`,
  duplicate: (label) => `Duplicate ${label}`,
};

const BUTTON_TEXT: Record<Mode, string> = {
  create: 'Create',
  edit: 'Save',
  duplicate: 'Duplicate',
};

export default function ShortlistFormModal({
  open,
  setOpen,
  mode,
  apiBase = '/shortlists',
  label = 'Shortlist',
  navigateTo,
  shortlistId,
  currentName = '',
  currentDownloadEnabled = true,
  fetchShortlists,
  updateShortlistName,
  onShortlistCreated,
  onDownloadEnabledChange,
}: Props) {
  const [name, setName] = useState(currentName);
  const [downloadEnabled, setDownloadEnabled] = useState(currentDownloadEnabled);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const isVideoShortlist = apiBase === '/video-shortlists';

  useEffect(() => {
    setName(currentName);
  }, [currentName]);

  useEffect(() => {
    setDownloadEnabled(currentDownloadEnabled);
  }, [currentDownloadEnabled]);

  const getErrorMessage = (error: any, defaultMessage: string): string => {
    const backendError = typeof error.response?.data === 'string'
      ? error.response.data
      : error.response?.data?.error || error.response?.data;
    if (error.response?.status === 500 && backendError === 'NameExists') {
      return 'Shortlist already exists with this name';
    }
    return backendError || error.message || defaultMessage;
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error(`${label} name is required`);
      return;
    }

    setLoading(true);
    try {
      if (mode === 'create') {
        const response = await axiosInstant.post(apiBase, {
          name: name,
          download_enabled: downloadEnabled,
        });
        const defaultNav = `/experts/shortlists/${response.data.id}?shortlist=${response.data.id}`;
        navigate(navigateTo ? `${navigateTo}/${response.data.id}` : defaultNav);
      } else if (mode === 'edit') {
        await axiosInstant.patch(`${apiBase}/${shortlistId}`, {
          name: name,
          download_enabled: downloadEnabled,
        });
        updateShortlistName?.(name);
        onDownloadEnabledChange?.(downloadEnabled);
        fetchShortlists?.();
        toast.success(`${label} updated successfully!`);
      } else if (mode === 'duplicate') {
        const dupUrl = `${apiBase}/${shortlistId}/duplicate${name ? `?name=${name}` : ''}`;
        const response = await axiosInstant.post(dupUrl);
        toast.success(`Duplicate ${label.toLowerCase()} "${response.data.name}" has been created!`);
        onShortlistCreated?.();
      }
      setOpen(false);
    } catch (error: any) {
      toast.error(getErrorMessage(error, `Failed to ${mode} ${label.toLowerCase()}`));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Box
        component={Paper}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: 'calc(100% - 24px)', sm: '400px' },
          maxWidth: '400px',
          maxHeight: { xs: 'calc(100vh - 24px)', sm: '90vh' },
          overflow: 'auto',
          bgcolor: 'var(--surface)',
          borderRadius: '12px',
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          padding: { xs: '16px', sm: '24px' },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
          <Typography sx={{ fontSize: '24px', fontWeight: 900 }}>
            {TITLES[mode](label)}
          </Typography>
          <IconButton onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <CustomFieldLabel>{label} Name</CustomFieldLabel>
        <TextField
          variant="outlined"
          fullWidth
          sx={textFieldStyles}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        {isVideoShortlist && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={downloadEnabled}
                  onChange={(e) => setDownloadEnabled(e.target.checked)}
                />
              }
              label="Download Settings"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontFamily: 'CircularStd',
                  fontSize: 14,
                  fontWeight: 500,
                },
              }}
            />
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: '12px' }}>
              {downloadEnabled ? 'Downloads are enabled on the share page' : 'Downloads are disabled on the share page'}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {BUTTON_TEXT[mode]}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
